<template>
    <div class="prelative">
        <div class="mb-4">
            <h4>{{ $filters.capitalize($l10n.__('contact')) }}</h4>

            <el-form
                v-if="Object.keys(billingContactTmp).length > 0"
                ref="billingContactForm"
                :model="billingContactTmp"
                labelPosition="top"
                :inlineMessage="true"
                class="well shadow"
                :rules="samBillingContactRules"
            >
                <el-form-item
                    :label="$t.__('common.labels.first_name')"
                    prop="contactFirstName"
                    :error="errors.get('contactFirstName')"
                >
                    <TextInput v-model="billingContactTmp.contactFirstName" mode="edit" />
                </el-form-item>

                <el-form-item
                    :label="$t.__('common.labels.name')"
                    prop="contactName"
                    :error="errors.get('contactName')"
                >
                    <TextInput v-model="billingContactTmp.contactName" mode="edit" />
                </el-form-item>

                <el-form-item
                    :label="$t.__('common.labels.email_address')"
                    prop="contactEmail"
                    :error="errors.get('contactEmail')"
                >
                    <TextInput v-model="billingContactTmp.contactEmail" type="email" mode="edit" />
                </el-form-item>

                <el-form-item
                    :label="$t.__('common.labels.phone')"
                    prop="contactPhone"
                    :error="errors.get('contactPhone')"
                >
                    <tel-input
                        v-model="billingContactPhoneTmp"
                        mode="edit"
                        @change="updateBilligContactPhoneTmp"
                    />
                </el-form-item>

                <el-form-item
                    :label="$t.__('common.labels.invoicing_language')"
                    prop="locale"
                    :error="errors.get('locale')"
                >
                    <SelectInput
                        v-model="billingContactTmp.locale"
                        mode="edit"
                        :options="billingLocales"
                        labelColumn="name"
                        idColumn="code"
                        :filterable="true"
                        class="m-0"
                    />
                </el-form-item>
            </el-form>
        </div>

        <el-form
            ref="billingAddressForm"
            :model="billingAddressTmp"
            labelPosition="top"
            :inlineMessage="true"
            class="well shadow"
            :rules="samBillingAddressAndIntraVatRules"
        >
            <!--  ADDRESS -->
            <div class="mb-4">
                <h4>{{ $filters.capitalize($l10n.__('adresse de facturation du client')) }}</h4>
                <el-form-item
                    :label="$t.__('common.labels.street_name')"
                    prop="line1"
                    :error="errors.get('line1')"
                >
                    <algolia-places
                        :defaultValue="billingAddressTmp.line1"
                        :templates="{
                            value: (suggestion) => {
                                return suggestion.name;
                            },
                        }"
                        @bubble-address="captureAddress"
                    ></algolia-places>
                </el-form-item>

                <el-form-item
                    :label="$t.__('common.labels.more_address_informations')"
                    prop="line2"
                    :error="errors.get('line2')"
                >
                    <TextInput v-model="billingAddressTmp.line2" mode="edit" />
                </el-form-item>

                <el-form-item
                    :label="$t.__('common.labels.postal_code')"
                    prop="postalCode"
                    :error="errors.get('postalCode')"
                >
                    <TextInput v-model="billingAddressTmp.postalCode" mode="edit" />
                </el-form-item>

                <el-form-item
                    :label="$t.__('common.labels.city')"
                    prop="city"
                    :error="errors.get('city')"
                >
                    <TextInput v-model="billingAddressTmp.city" mode="edit" />
                </el-form-item>

                <el-form-item
                    :label="$t.__('common.labels.state')"
                    prop="state"
                    :error="errors.get('state')"
                >
                    <TextInput v-model="billingAddressTmp.state" mode="edit" />
                </el-form-item>

                <el-form-item
                    v-if="Object.keys(countries).length"
                    :label="$t.__('common.labels.country')"
                    prop="country"
                    :error="errors.get('country')"
                >
                    <SelectInput
                        v-model="billingAddressTmp.country"
                        mode="edit"
                        :options="countries"
                        labelColumn="name"
                        idColumn="code"
                        :filterable="true"
                    />
                </el-form-item>
            </div>
            <!--  VAT IDENTIFIER -->
            <div v-if="hasCountryIntraVat(billingAddressTmp.country)" class="mb-4">
                <h4>{{ $filters.capitalize($l10n.__('Informations légales')) }}</h4>
                <el-form-item
                    :label="$t.__('common.labels.tax_number')"
                    prop="vatIdentificationNumber"
                    :error="errors.get('vatIdentificationNumber')"
                >
                    <TextInput v-model="billingAddressTmp.vatIdentificationNumber" mode="edit" />
                </el-form-item>
            </div>
        </el-form>

        <div class="aright">
            <CompassButton :label="$t.__('common.actions.save')" @click="saveInformations" />
        </div>
    </div>
</template>

<script>
import SAMMixins from '../../../mixins/subscription/SAMMixins';
import AlgoliaInput from '../../inputs/AlgoliaInput.vue';
import TelInput from '../../inputs/TelInput.vue';
import FormMixins from '../../../mixins/FormMixins';
import WidgetMixins from '../../../mixins/sellsyaccountmanager/widgetMixins';
import SelectInput from 'vcomponents/inputs/SelectInput.vue';
import TextInput from 'vcomponents/inputs/TextInput.vue';

export default {
    components: {
        'algolia-places': AlgoliaInput,
        SelectInput,
        TextInput,
        'tel-input': TelInput,
    },
    mixins: [SAMMixins, FormMixins, WidgetMixins],
    props: {
        corporation: {
            type: [String, Number],
            required: true,
        },
    },
    data() {
        return {
            billingSettingsForm: {},
        };
    },
    computed: {
        isBillingAddressValid() {
            return this.isAddressValid(this.billingAddressTmp);
        },
        isBillingContactValid() {
            return this.isContactValid(this.billingContactTmp, this.billingContactPhoneTmp);
        },
        billingAddressHasChanged() {
            return !_.isEqual(this.billingAddress, this.billingAddressTmp);
        },
        billingContactHasChanged() {
            const contactProperties = ['contactName', 'contactFirstName', 'contactEmail', 'locale'];
            return (
                !_.isEqual(
                    _.pick(this.billingContact, contactProperties),
                    _.pick(this.billingContactTmp, contactProperties),
                ) || this.billingSettings.contactPhone !== this.billingContactPhoneTmp.e164
            );
        },
    },
    created() {
        this.getCountries().then((countries) => this.setCountries(countries));
        this.getBillingLocales().then((billingLocales) => this.setBillingLocales(billingLocales));
        this.initBillingContactTmp();
        this.initBillingAddressTmp();
    },
    methods: {
        captureAddress(address) {
            this.billingAddressTmp.line1 = address.name;
            this.billingAddressTmp.line2 = '';
            this.billingAddressTmp.postalCode = address.postcode;
            this.billingAddressTmp.city = address.city;
            this.billingAddressTmp.country = address.countryCode
                ? address.countryCode.toUpperCase()
                : 'FR';
            this.billingAddressTmp.state = address.administrative;
        },
        isValid() {
            return new Promise((resolve, reject) => {
                if (!this.isBillingContactValid) {
                    throw new Error('billingcontact');
                } else if (!this.isBillingAddressValid) {
                    throw new Error('billingaddress');
                }
                resolve(true);
            });
        },
        saveInformations() {
            let hasChanges = false;
            this.$emit('load', true);
            this.isValid()
                .then(() => {
                    // billingAddress
                    // - is record needed ?
                    if (!this.billingAddressHasChanged) return;
                    // record
                    hasChanges = true;
                    return this.recordBillingAddress(this.corporation, this.billingAddressTmp).then(
                        (address) => this.setBillingAddress(address),
                    );
                })
                .then(() => {
                    // billingContact
                    // - is record needed ?
                    if (!this.billingContactHasChanged) return;
                    // record
                    hasChanges = true;
                    return this.recordBillingContact(
                        this.corporation,
                        this.billingContactTmp,
                        this.billingContactPhoneTmp,
                    ).then((billingSettings) => this.setBillingContact(billingSettings));
                })
                .then(() => {
                    // vatIdentifier
                    // - is record needed ?
                    if (!this.billingSettingsHasChanged) return;
                    // record
                    hasChanges = true;
                    return this.recordBillingSettings(this.corporation, {
                        vatIdentificationNumber: this.billingAddressTmp.vatIdentificationNumber,
                    }).then((billingSettings) => this.setBillingSettings(billingSettings));
                })
                .then(() => {
                    hasChanges
                        ? Push.success(this.$l10n.__('Changements effectuées'))
                        : Push.warning(this.$l10n.__('Aucun changement observé'));
                })
                .catch((e) => {
                    switch (e.message) {
                        case 'billingcontact':
                            Push.error(
                                this.$l10n.__('Certains champs sont invalides ou incomplets'),
                                this.$l10n.__('Contact de facturation'),
                            );
                            break;
                        case 'billingaddress':
                            Push.error(
                                this.$l10n.__('Certains champs sont invalides ou incomplets'),
                                this.$l10n.__('Adresse de facturation'),
                            );
                            break;
                        default:
                            Push.error(this.$l10n.__('Une erreur est survenue'));
                            break;
                    }
                })
                .finally((e) => this.$emit('load', false));
        },
    },
};
</script>
